import { useState, useCallback } from 'react';
import { taskRequestApi } from '../services/taskRequestApi';
import { useApiMutation } from '@hooks/useApiMutation';

interface RejectTaskDialogProps {
  taskId: number;
  onRejected: () => void;
  onCancel: () => void;
}

export default function RejectTaskDialog({
  taskId,
  onRejected,
  onCancel,
}: Readonly<RejectTaskDialogProps>) {
  const [reason, setReason] = useState('');

  const maxLength = 1000;

  const rejectMutation = useApiMutation(
    useCallback(async () => {
      await taskRequestApi.rejectTaskRequest(taskId, {
        rejectionReason: reason.trim(),
      });
    }, [taskId, reason]),
    { onSuccess: onRejected, errorMessage: 'Failed to reject task. Please try again.' },
  );

  const handleSubmit = async () => {
    if (!reason.trim() || rejectMutation.loading) return;
    await rejectMutation.mutate();
  };

  return (
    <div className="task-dialog-overlay" onClick={onCancel}>
      <div className="task-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="task-dialog-header">
          <h3>Reject Task</h3>
        </div>
        <div className="task-dialog-body">
          {rejectMutation.error && (
            <p className="task-dialog-error">
              {rejectMutation.error}
            </p>
          )}

          <div className="task-dialog-field">
            <label htmlFor="reject-task-reason">Rejection Reason (required)</label>
            <textarea
              id="reject-task-reason"
              placeholder="Explain why this task is being rejected..."
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, maxLength))}
              rows={4}
              autoFocus
            />
            <div className="task-dialog-char-count">
              {reason.length}/{maxLength}
            </div>
          </div>
        </div>
        <div className="task-dialog-footer">
          <button
            type="button"
            className="task-action-btn task-action-btn-secondary"
            onClick={onCancel}
            disabled={rejectMutation.loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="task-action-btn task-action-btn-danger"
            onClick={handleSubmit}
            disabled={!reason.trim() || rejectMutation.loading}
          >
            {rejectMutation.loading ? 'Rejecting...' : 'Reject Task'}
          </button>
        </div>
      </div>
    </div>
  );
}
